import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import {
	CartesianGrid,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	Scatter,
	ScatterChart,
	XAxis,
	YAxis,
} from "recharts";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { PublicFooter } from "@/components/public-layout";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useFileUrls } from "@/hooks/use-file-url";
import { type Currency, currencyAtom } from "@/lib/currency";
import { formatCompactCurrency } from "@/lib/format";
import { publicFoundersQueryOptions } from "@/lib/founders-list.query";
import { buildLocalizedSeoLinks, ogImageUrl } from "@/lib/seo";
import { xAvatarUrl } from "@/lib/validation";

type FounderListItem = {
	avatarUrl: string | null;
	userImage: string | null;
	name: string;
	slug: string;
	tagline: string | null;
	xHandle: string | null;
	stats: {
		allTimeRevenue: number;
		estimatedMrr: number;
		totalAgents: number;
	};
};

type Metric = "mrr" | "revenue";

const METRIC_CONFIG: Record<
	Metric,
	{ label: string; axisLabel: string; value: (f: FounderListItem) => number }
> = {
	mrr: {
		label: "MRR",
		axisLabel: "Monthly recurring revenue (MRR)",
		value: (f) => f.stats.estimatedMrr,
	},
	revenue: {
		label: "Total revenue",
		axisLabel: "All-time total revenue",
		value: (f) => f.stats.allTimeRevenue,
	},
};

type ChartPoint = {
	x: number;
	y: number;
	name: string;
	xHandle: string | null;
	avatarUrl: string | null;
	mrr: number;
	revenue: number;
	agents: number;
};

export const Route = createFileRoute("/stats")({
	component: StatsPage,
	loader: ({ context }) =>
		context.queryClient.ensureQueryData(publicFoundersQueryOptions),
	head: ({ matches }) => {
		const desc =
			"Distribution of every founder on AgentMRR plotted by provider-verified MRR and total revenue against the number of agents they ship.";
		return {
			meta: [
				{ title: "Founder stats - AgentMRR" },
				{ name: "description", content: desc },
				{ property: "og:title", content: "Founder stats - AgentMRR" },
				{ property: "og:description", content: desc },
				{ property: "og:image", content: ogImageUrl("default") },
				{ property: "og:type", content: "website" },
				{ property: "og:site_name", content: "AgentMRR" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("default") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/stats" }),
		};
	},
});

function avatarSource(f: FounderListItem): string | null {
	return f.avatarUrl ?? f.userImage ?? xAvatarUrl(f.xHandle) ?? null;
}

function StatsPage() {
	const currency = useAtomValue(currencyAtom);
	const foundersQuery = useQuery(publicFoundersQueryOptions);
	const founders = (foundersQuery.data?.founders ?? []) as FounderListItem[];
	const isLoading = foundersQuery.isLoading && founders.length === 0;
	const [metric, setMetric] = useState<Metric>("mrr");

	const { urlMap } = useFileUrls(
		founders.map((f) => avatarSource(f)).filter((v): v is string => !!v),
	);

	const points = useMemo<ChartPoint[]>(() => {
		const value = METRIC_CONFIG[metric].value;
		return founders
			.map((f) => {
				const src = avatarSource(f);
				return {
					x: value(f),
					y: f.stats.totalAgents,
					name: f.name,
					xHandle: f.xHandle,
					avatarUrl: src ? (urlMap.get(src) ?? null) : null,
					mrr: f.stats.estimatedMrr,
					revenue: f.stats.allTimeRevenue,
					agents: f.stats.totalAgents,
				};
			})
			.filter((p) => p.x > 0);
	}, [founders, metric, urlMap]);

	const hiddenCount = founders.length - points.length;
	const maxAgents = points.reduce((m, p) => Math.max(m, p.y), 1);

	return (
		<PublicPageLayout>
			<main className="mx-auto w-full max-w-5xl pt-4 pb-16 sm:pt-6">
				<PageBreadcrumb
					items={[
						{ label: "Stats", to: "/" },
						{ label: "Founder distribution" },
					]}
				/>

				<section className="mt-10 border-t border-border pt-3">
					<div className="pb-3">
						<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
							&gt; Stats
						</span>
					</div>
					<h1
						className="font-mono text-[clamp(1.75rem,4vw,2.75rem)] leading-[1.05] tracking-[-0.02em] text-foreground"
						style={{ fontWeight: 400 }}
					>
						Founder distribution by {METRIC_CONFIG[metric].label}
					</h1>
					<p className="mt-3 max-w-2xl text-base leading-[1.6] text-muted-foreground">
						Every founder plotted by {METRIC_CONFIG[metric].label.toLowerCase()}{" "}
						(log scale) against the number of agents they ship. Hover an avatar
						for details.
					</p>
				</section>

				<section className="mt-10">
					<div className="mb-4 flex items-center justify-between gap-3">
						<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
							{points.length} founders
							{hiddenCount > 0 ? ` · ${hiddenCount} with $0 hidden` : ""}
						</span>
						<Select
							value={metric}
							onValueChange={(next) => setMetric(next as Metric)}
						>
							<SelectTrigger className="w-[180px] font-mono text-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="mrr">MRR</SelectItem>
								<SelectItem value="revenue">Total revenue</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{isLoading ? (
						<div className="h-[560px] w-full animate-pulse border border-border bg-card" />
					) : points.length === 0 ? (
						<p className="border border-border bg-card px-4 py-8 text-center font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							No founder revenue data yet.
						</p>
					) : (
						<DistributionChart
							points={points}
							metric={metric}
							currency={currency}
							maxAgents={maxAgents}
						/>
					)}
				</section>

				<div className="mt-16">
					<PublicFooter />
				</div>
			</main>
		</PublicPageLayout>
	);
}

function DistributionChart({
	points,
	metric,
	currency,
	maxAgents,
}: {
	points: ChartPoint[];
	metric: Metric;
	currency: Currency;
	maxAgents: number;
}) {
	return (
		<div className="border border-border bg-card p-4 sm:p-6">
			<ResponsiveContainer width="100%" height={560}>
				<ScatterChart margin={{ top: 16, right: 24, bottom: 48, left: 16 }}>
					<CartesianGrid
						strokeDasharray="4 4"
						className="stroke-border"
						strokeOpacity={0.5}
					/>
					<XAxis
						type="number"
						dataKey="x"
						name={METRIC_CONFIG[metric].axisLabel}
						scale="log"
						domain={[1, "dataMax"]}
						allowDataOverflow
						tickFormatter={(v: number) => formatCompactCurrency(v, currency)}
						tick={{ fontSize: 12, fontFamily: "monospace" }}
						className="fill-muted-foreground"
						stroke="currentColor"
						label={{
							value: METRIC_CONFIG[metric].axisLabel,
							position: "bottom",
							offset: 24,
							style: {
								fontFamily: "monospace",
								fontSize: 13,
								fill: "currentColor",
							},
						}}
					/>
					<YAxis
						type="number"
						dataKey="y"
						name="Agents listed"
						domain={[0, maxAgents + 1]}
						allowDecimals={false}
						tick={{ fontSize: 12, fontFamily: "monospace" }}
						className="fill-muted-foreground"
						stroke="currentColor"
						label={{
							value: "Agents listed",
							angle: -90,
							position: "insideLeft",
							style: {
								fontFamily: "monospace",
								fontSize: 13,
								fill: "currentColor",
								textAnchor: "middle",
							},
						}}
					/>
					<RechartsTooltip
						cursor={{ strokeDasharray: "4 4", className: "stroke-border" }}
						content={<PointTooltip currency={currency} />}
					/>
					<Scatter
						data={points}
						shape={renderAvatarDot}
						isAnimationActive={false}
					/>
				</ScatterChart>
			</ResponsiveContainer>
		</div>
	);
}

const DOT = 28;

// recharts calls this per-point during render; no hooks allowed here.
function renderAvatarDot(props: {
	cx?: number;
	cy?: number;
	payload?: ChartPoint;
}) {
	const { cx, cy, payload } = props;
	if (cx == null || cy == null || !payload) return <g />;
	const x = cx - DOT / 2;
	const y = cy - DOT / 2;

	if (payload.avatarUrl) {
		const clipId = `avatar-clip-${payload.name.replace(/[^a-zA-Z0-9]/g, "")}-${Math.round(cx)}-${Math.round(cy)}`;
		return (
			<g>
				<title>{payload.name}</title>
				<clipPath id={clipId}>
					<rect x={x} y={y} width={DOT} height={DOT} />
				</clipPath>
				<image
					href={payload.avatarUrl}
					x={x}
					y={y}
					width={DOT}
					height={DOT}
					clipPath={`url(#${clipId})`}
					preserveAspectRatio="xMidYMid slice"
				/>
				<rect
					x={x}
					y={y}
					width={DOT}
					height={DOT}
					fill="none"
					className="stroke-border"
					strokeWidth={1}
				/>
			</g>
		);
	}

	const initials = payload.name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase())
		.join("");
	return (
		<g>
			<title>{payload.name}</title>
			<rect
				x={x}
				y={y}
				width={DOT}
				height={DOT}
				className="fill-muted stroke-border"
				strokeWidth={1}
			/>
			<text
				x={cx}
				y={cy}
				textAnchor="middle"
				dominantBaseline="central"
				fontFamily="monospace"
				fontSize={11}
				className="fill-foreground"
			>
				{initials}
			</text>
		</g>
	);
}

function PointTooltip({
	active,
	payload,
	currency,
}: {
	active?: boolean;
	payload?: Array<{ payload: ChartPoint }>;
	currency: Currency;
}) {
	if (!active || !payload?.length) return null;
	const p = payload[0].payload;
	return (
		<div className="border border-border bg-card px-3 py-2 shadow-md">
			<p className="text-sm text-foreground" style={{ fontWeight: 400 }}>
				{p.name}
			</p>
			{p.xHandle ? (
				<p className="font-mono text-xs text-muted-foreground">@{p.xHandle}</p>
			) : null}
			<dl className="mt-2 grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 font-mono text-xs">
				<dt className="text-muted-foreground">MRR</dt>
				<dd className="text-right tabular-nums text-foreground">
					{formatCompactCurrency(p.mrr, currency)}
				</dd>
				<dt className="text-muted-foreground">Revenue</dt>
				<dd className="text-right tabular-nums text-foreground">
					{formatCompactCurrency(p.revenue, currency)}
				</dd>
				<dt className="text-muted-foreground">Agents</dt>
				<dd className="text-right tabular-nums text-foreground">{p.agents}</dd>
			</dl>
		</div>
	);
}
